/**
 * App-wide notifications & confirmations (top, compact — aligns with form modal top position).
 */
import { toast } from "react-toastify";

export const TOAST_POSITION = "top-center";

const defaultToastOpts = {
  position: TOAST_POSITION,
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
  className: "app-toast-compact",
};

export const notify = {
  success: (message, options = {}) =>
    toast.success(String(message ?? ""), { ...defaultToastOpts, ...options }),
  error: (message, options = {}) =>
    toast.error(String(message ?? ""), { ...defaultToastOpts, ...options }),
  info: (message, options = {}) =>
    toast.info(String(message ?? ""), { ...defaultToastOpts, ...options }),
  warn: (message, options = {}) =>
    toast.warn(String(message ?? ""), { ...defaultToastOpts, ...options }),
  warning: (message, options = {}) =>
    toast.warn(String(message ?? ""), { ...defaultToastOpts, ...options }),
};

/** @type {null | ((message: string, options?: object) => Promise<boolean>)} */
let confirmHandler = null;

export const registerConfirmHandler = (handler) => {
  confirmHandler = typeof handler === "function" ? handler : null;
};

/**
 * Promise-based confirmation (centered compact dialog).
 * Usage: if (!(await confirmAsync("Delete this item?"))) return;
 */
export const confirmAsync = (message, options = {}) => {
  if (typeof confirmHandler === "function") {
    return confirmHandler(String(message ?? ""), options);
  }
  console.warn(
    "[appNotify] Confirm provider not ready; defaulting to false for:",
    message
  );
  return Promise.resolve(false);
};

/**
 * Override native browser popups so existing alert()/window.alert() become toasts.
 * Native confirm cannot be made async safely — call sites should use confirmAsync.
 */
export const installBrowserPopupOverrides = () => {
  if (typeof window === "undefined") return;

  window.alert = (message) => {
    const text = String(message ?? "");
    const lower = text.toLowerCase();
    if (
      lower.includes("success") ||
      lower.includes("saved") ||
      lower.includes("created") ||
      lower.includes("updated") ||
      lower.includes("submitted")
    ) {
      notify.success(text);
    } else if (
      lower.includes("fail") ||
      lower.includes("error") ||
      lower.includes("unable") ||
      lower.includes("invalid") ||
      lower.includes("required")
    ) {
      notify.error(text);
    } else if (lower.includes("warning") || lower.includes("warn")) {
      notify.warn(text);
    } else {
      notify.info(text);
    }
  };

  window.confirm = (message) => {
    console.warn(
      "[appNotify] Synchronous window.confirm() is disabled. Use confirmAsync(). Message:",
      message
    );
    confirmAsync(String(message ?? ""), {
      title: "Please confirm",
      confirmLabel: "OK",
      cancelLabel: "Cancel",
    });
    return false;
  };
};

export default notify;
